import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNotification } from '../common';

const ModulesSettings = () => {
  const { showNotification } = useNotification();
  const [modules, setModules] = useState({
    videoCourses: false,
    prospector: false,
    outlookCalendar: false,
    contracts: false,
    intercom: false,
    mixpanel: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [outlookConnectionStatus, setOutlookConnectionStatus] = useState({
    connected: false,
    loading: false,
    connecting: false,
    expires_at: null,
    is_expired: false
  });
  const [intercomSettings, setIntercomSettings] = useState({
    app_id: '',
    access_token: '',
    enabled: false,
    webhook_url: '',
    loading: false
  });
  const [showIntercomConfig, setShowIntercomConfig] = useState(false);
  const [mixpanelSettings, setMixpanelSettings] = useState({
    project_token: '',
    api_secret: '',
    sync_enabled: true,
    configured: false,
    loading: false
  });
  const [showMixpanelConfig, setShowMixpanelConfig] = useState(false);

  // Load current module settings
  useEffect(() => {
    fetchModuleSettings();
  }, []);

  // Check Outlook connection status when module is enabled
  useEffect(() => {
    if (modules.outlookCalendar) {
      checkOutlookConnectionStatus();
    }
  }, [modules.outlookCalendar]);

  // Check Intercom settings when module is enabled
  useEffect(() => {
    if (modules.intercom) {
      fetchIntercomSettings();
    }
  }, [modules.intercom]);

  // Check Mixpanel settings when module is enabled
  useEffect(() => {
    if (modules.mixpanel) {
      fetchMixpanelSettings();
    }
  }, [modules.mixpanel]);

  const fetchModuleSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/settings/modules', {
        headers: { 'x-auth-token': token }
      });

      if (response.data) {
        setModules({
          videoCourses: response.data.video_courses_enabled || false,
          prospector: response.data.prospector_enabled || false,
          outlookCalendar: response.data.outlook_calendar_enabled || false,
          contracts: response.data.contracts_enabled || false,
          intercom: response.data.intercom_enabled || false,
          mixpanel: response.data.mixpanel_enabled || false
        });
      }
    } catch (error) {
      console.error('Error fetching module settings:', error);
      showNotification('Failed to load module settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleModuleToggle = async (moduleName, enabled) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      const updateData = {};
      if (moduleName === 'videoCourses') {
        updateData.video_courses_enabled = enabled;
      } else if (moduleName === 'prospector') {
        updateData.prospector_enabled = enabled;
      } else if (moduleName === 'outlookCalendar') {
        updateData.outlook_calendar_enabled = enabled;
      } else if (moduleName === 'contracts') {
        updateData.contracts_enabled = enabled;
      } else if (moduleName === 'intercom') {
        updateData.intercom_enabled = enabled;
      } else if (moduleName === 'mixpanel') {
        updateData.mixpanel_enabled = enabled;
      }

      await axios.put('/api/settings/modules', updateData, {
        headers: { 'x-auth-token': token }
      });

      setModules(prev => ({
        ...prev,
        [moduleName]: enabled
      }));

      showNotification(`${getModuleLabel(moduleName)} ${enabled ? 'enabled' : 'disabled'} successfully`, 'success');
      
      // Refresh the page to update navigation
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Error updating module settings:', error);
      showNotification('Failed to update module settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const getModuleLabel = (moduleName) => {
    switch (moduleName) {
      case 'videoCourses':
        return 'Video Courses';
      case 'prospector':
        return 'Prospector';
      case 'outlookCalendar':
        return 'Outlook Integration';
      case 'contracts':
        return 'Contract Management';
      case 'intercom':
        return 'Intercom Integration';
      case 'mixpanel':
        return 'Mixpanel Integration';
      default:
        return moduleName;
    }
  };

  const getModuleDescription = (moduleName) => {
    switch (moduleName) {
      case 'videoCourses':
        return 'Enable video course management and student access to educational content.';
      case 'prospector':
        return 'Enable company research and lead prospecting tools for finding new business opportunities. Currently supports Norwegian companies only.';
      case 'outlookCalendar':
        return 'Enable Microsoft Outlook integration for calendar events and email. View Outlook events in your CRM calendar and send/read emails through Outlook.';
      case 'contracts':
        return 'Enable digital contract creation, signing, and management with built-in e-signature capabilities. Generate contracts from deals and track signing progress.';
      case 'intercom':
        return 'Enable Intercom integration to sync customer conversations and support messages directly into lead records. View conversation history and sync new messages automatically.';
      case 'mixpanel':
        return 'Enable Mixpanel integration to view your clients\' website and app activity directly in lead records. See page views, events, and user behavior from their Mixpanel analytics.';
      default:
        return '';
    }
  };

  const checkOutlookConnectionStatus = async () => {
    try {
      setOutlookConnectionStatus(prev => ({ ...prev, loading: true }));
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/outlook-calendar/status', {
        headers: { 'x-auth-token': token }
      });
      
      setOutlookConnectionStatus({
        connected: response.data.connected,
        loading: false,
        connecting: false,
        expires_at: response.data.expires_at,
        is_expired: response.data.is_expired
      });
    } catch (error) {
      console.error('Error checking Outlook connection status:', error);
      setOutlookConnectionStatus({
        connected: false,
        loading: false,
        connecting: false,
        expires_at: null,
        is_expired: false
      });
    }
  };

  const handleOutlookConnect = async () => {
    try {
      setOutlookConnectionStatus(prev => ({ ...prev, connecting: true }));
      const token = localStorage.getItem('token');
      
      // Get authorization URL
      const response = await axios.get('/api/outlook-calendar/auth-url', {
        headers: { 'x-auth-token': token }
      });
      
      if (response.data.auth_url) {
        // Open OAuth popup window
        const popup = window.open(
          response.data.auth_url,
          'outlook-auth',
          'width=600,height=700,scrollbars=yes,resizable=yes'
        );
        
        // Listen for popup messages
        const messageHandler = async (event) => {
          console.log('📨 Message received from popup:', event.data);
          console.log('Event origin:', event.origin);
          console.log('Window origin:', window.location.origin);
          
          if (event.origin !== window.location.origin) {
            console.log('❌ Origin mismatch, ignoring message');
            return;
          }
          
          if (event.data.type === 'OUTLOOK_AUTH_SUCCESS') {
            console.log('✅ Auth success message received');
            popup.close();
            window.removeEventListener('message', messageHandler);
            
            // Handle successful authorization
            try {
              console.log('🔄 Making OAuth callback request to backend...');
              const response = await axios.post('/api/outlook-calendar/oauth-callback', {
                code: event.data.code,
                state: event.data.state
              });
              console.log('✅ Backend callback successful:', response.data);
              
              showNotification('Outlook connected successfully!', 'success');
              checkOutlookConnectionStatus();
            } catch (error) {
              console.error('❌ OAuth callback error:', error);
              console.error('Error details:', error.response?.data);
              showNotification('Failed to complete Outlook connection', 'error');
            }
          } else if (event.data.type === 'OUTLOOK_AUTH_ERROR') {
            popup.close();
            window.removeEventListener('message', messageHandler);
            showNotification('Outlook authorization failed', 'error');
          }
        };
        
        window.addEventListener('message', messageHandler);
        
        // Check if popup was closed without completing auth
        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed);
            window.removeEventListener('message', messageHandler);
            setOutlookConnectionStatus(prev => ({ ...prev, connecting: false }));
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Error initiating Outlook connection:', error);
      if (error.response?.data?.message?.includes('not configured')) {
        showNotification('Microsoft OAuth is not configured on this server. Please contact your administrator to set up the integration.', 'error');
      } else {
        showNotification('Failed to start Outlook connection', 'error');
      }
      setOutlookConnectionStatus(prev => ({ ...prev, connecting: false }));
    }
  };

  const handleOutlookDisconnect = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete('/api/outlook-calendar/disconnect', {
        headers: { 'x-auth-token': token }
      });
      
      showNotification('Outlook Calendar disconnected successfully', 'success');
      setOutlookConnectionStatus({
        connected: false,
        loading: false,
        connecting: false,
        expires_at: null,
        is_expired: false
      });
    } catch (error) {
      console.error('Error disconnecting Outlook:', error);
      showNotification('Failed to disconnect Outlook Calendar', 'error');
    }
  };

  const fetchIntercomSettings = async () => {
    try {
      setIntercomSettings(prev => ({ ...prev, loading: true }));
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/intercom/settings', {
        headers: { 'x-auth-token': token }
      });
      
      console.log('Intercom settings response:', response.data);
      
      setIntercomSettings({
        ...response.data,
        loading: false
      });
    } catch (error) {
      console.error('Error fetching Intercom settings:', error);
      setIntercomSettings(prev => ({ ...prev, loading: false }));
    }
  };

  const saveIntercomSettings = async (settings) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/intercom/settings', settings, {
        headers: { 'x-auth-token': token }
      });
      
      showNotification('Intercom settings saved successfully', 'success');
      await fetchIntercomSettings();
      setShowIntercomConfig(false);
    } catch (error) {
      console.error('Error saving Intercom settings:', error);
      showNotification(error.response?.data?.error || 'Failed to save Intercom settings', 'error');
    }
  };

  const testIntercomConnection = async (accessToken) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/intercom/test-connection', {
        access_token: accessToken
      }, {
        headers: { 'x-auth-token': token }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error testing Intercom connection:', error);
      throw error;
    }
  };

  const fetchMixpanelSettings = async () => {
    try {
      setMixpanelSettings(prev => ({ ...prev, loading: true }));
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/mixpanel-client/settings', {
        headers: { 'x-auth-token': token }
      });
      
      console.log('Mixpanel settings response:', response.data);
      
      setMixpanelSettings({
        ...response.data,
        loading: false
      });
    } catch (error) {
      console.error('Error fetching Mixpanel settings:', error);
      setMixpanelSettings(prev => ({ ...prev, loading: false }));
    }
  };

  const saveMixpanelSettings = async (settings) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/mixpanel-client/settings', settings, {
        headers: { 'x-auth-token': token }
      });
      
      showNotification('Mixpanel settings saved successfully', 'success');
      await fetchMixpanelSettings();
      setShowMixpanelConfig(false);
    } catch (error) {
      console.error('Error saving Mixpanel settings:', error);
      showNotification(error.response?.data?.error || 'Failed to save Mixpanel settings', 'error');
    }
  };

  const testMixpanelConnection = async (projectToken, apiSecret) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/mixpanel-client/test-connection', {
        project_token: projectToken,
        api_secret: apiSecret
      }, {
        headers: { 'x-auth-token': token }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error testing Mixpanel connection:', error);
      throw error;
    }
  };

  const getModuleIcon = (moduleName) => {
    switch (moduleName) {
      case 'videoCourses':
        return (
          <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        );
      case 'prospector':
        return (
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        );
      case 'outlookCalendar':
        return (
          <img src="/icons/outlook.svg" alt="Outlook" className="w-8 h-8" />
        );
      case 'contracts':
        return (
          <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'intercom':
        return (
          <img src="/icons/intercom.svg" alt="Intercom" className="w-8 h-8" />
        );
      case 'mixpanel':
        return (
          <img src="/icons/mixpanel.svg" alt="Mixpanel" className="w-8 h-8" />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Module Management</h3>
          <p className="mt-1 text-sm text-gray-500">
            Enable or disable modules for your company. Changes will take effect immediately and affect all users.
          </p>
        </div>

        <div className="p-6">
          <div className="space-y-6">
            {Object.entries(modules).map(([moduleName, isEnabled]) => (
              <div key={moduleName} className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg">
                <div className="flex-shrink-0">
                  {getModuleIcon(moduleName)}
                </div>
                
                <div className="flex-grow">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-lg font-medium text-gray-900">
                        {getModuleLabel(moduleName)}
                      </h4>
                      <p className="text-sm text-gray-500 mt-1">
                        {getModuleDescription(moduleName)}
                      </p>
                      {moduleName === 'prospector' && (
                        <div className="mt-2 flex items-center text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                          <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          Norwegian companies only
                        </div>
                      )}
                      
                      {/* Outlook Calendar Connection Status */}
                      {moduleName === 'outlookCalendar' && isEnabled && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              {outlookConnectionStatus.loading ? (
                                <div className="flex items-center text-sm text-gray-600">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                                  Checking connection...
                                </div>
                              ) : outlookConnectionStatus.connected && !outlookConnectionStatus.is_expired ? (
                                <div className="flex items-center text-sm text-green-600">
                                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                  Connected to Outlook
                                </div>
                              ) : outlookConnectionStatus.is_expired ? (
                                <div className="flex items-center text-sm text-yellow-600">
                                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                  Connection expired
                                </div>
                              ) : (
                                <div className="flex items-center text-sm text-gray-500">
                                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Not connected
                                </div>
                              )}
                            </div>
                            
                            <div className="flex space-x-2">
                              {outlookConnectionStatus.connected && !outlookConnectionStatus.is_expired ? (
                                <button
                                  onClick={handleOutlookDisconnect}
                                  className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                                >
                                  Disconnect
                                </button>
                              ) : (
                                <button
                                  onClick={handleOutlookConnect}
                                  disabled={outlookConnectionStatus.connecting}
                                  className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  {outlookConnectionStatus.connecting ? 'Connecting...' : 'Connect'}
                                </button>
                              )}
                            </div>
                          </div>
                          
                        </div>
                      )}

                      {/* Intercom Configuration */}
                      {moduleName === 'intercom' && isEnabled && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center">
                              {intercomSettings.loading ? (
                                <div className="flex items-center text-sm text-gray-600">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-500 mr-2"></div>
                                  Loading settings...
                                </div>
                              ) : intercomSettings.app_id ? (
                                <div className="flex items-center text-sm text-green-600">
                                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                  Configured (App ID: {intercomSettings.app_id})
                                </div>
                              ) : (
                                <div className="flex items-center text-sm text-gray-500">
                                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Not configured
                                </div>
                              )}
                            </div>
                            
                            <button
                              onClick={() => setShowIntercomConfig(!showIntercomConfig)}
                              className="px-3 py-1 text-xs bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 transition-colors"
                            >
                              Configure
                            </button>
                          </div>

                          {intercomSettings.webhook_url && (
                            <div className="text-xs text-gray-600 mb-2">
                              <strong>Webhook URL:</strong> {intercomSettings.webhook_url}
                            </div>
                          )}

                          {/* Intercom Configuration Modal/Form */}
                          {showIntercomConfig && (
                            <IntercomConfigForm
                              settings={intercomSettings}
                              onSave={saveIntercomSettings}
                              onCancel={() => setShowIntercomConfig(false)}
                              onTest={testIntercomConnection}
                            />
                          )}
                        </div>
                      )}

                      {/* Mixpanel Configuration */}
                      {moduleName === 'mixpanel' && isEnabled && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center">
                              {mixpanelSettings.loading ? (
                                <div className="flex items-center text-sm text-gray-600">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500 mr-2"></div>
                                  Loading settings...
                                </div>
                              ) : mixpanelSettings.configured ? (
                                <div className="flex items-center text-sm text-green-600">
                                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                  Configured (Project: {mixpanelSettings.project_token})
                                </div>
                              ) : (
                                <div className="flex items-center text-sm text-gray-500">
                                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Not configured
                                </div>
                              )}
                            </div>
                            
                            <button
                              onClick={() => setShowMixpanelConfig(!showMixpanelConfig)}
                              className="px-3 py-1 text-xs bg-orange-100 text-orange-700 rounded-md hover:bg-orange-200 transition-colors"
                            >
                              Configure
                            </button>
                          </div>

                          {/* Mixpanel Configuration Modal/Form */}
                          {showMixpanelConfig && (
                            <MixpanelConfigForm
                              settings={mixpanelSettings}
                              onSave={saveMixpanelSettings}
                              onCancel={() => setShowMixpanelConfig(false)}
                              onTest={testMixpanelConnection}
                            />
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center">
                      <button
                        type="button"
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          isEnabled ? 'bg-blue-600' : 'bg-gray-200'
                        }`}
                        role="switch"
                        aria-checked={isEnabled}
                        onClick={() => handleModuleToggle(moduleName, !isEnabled)}
                        disabled={saving}
                      >
                        <span
                          aria-hidden="true"
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            isEnabled ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                      <span className="ml-3 text-sm">
                        <span className={`font-medium ${isEnabled ? 'text-green-600' : 'text-gray-500'}`}>
                          {isEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {saving && (
            <div className="mt-6 flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
              <span className="text-sm text-gray-600">Updating modules...</span>
            </div>
          )}
        </div>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">Important Notice</h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                Disabling a module will immediately hide it from the navigation for all users in your company. 
                Any data associated with disabled modules will be preserved and restored when the module is re-enabled.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Intercom Configuration Form Component
const IntercomConfigForm = ({ settings, onSave, onCancel, onTest }) => {
  const [formData, setFormData] = useState({
    app_id: settings.app_id || '',
    access_token: settings.access_token || '',
    enabled: settings.enabled || false
  });
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        ...formData,
        enabled: true
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!formData.access_token) {
      alert('Please enter an access token first');
      return;
    }

    setTesting(true);
    setTestResult(null);
    
    try {
      const result = await onTest(formData.access_token);
      setTestResult(result);
    } catch (error) {
      setTestResult({
        success: false,
        error: error.response?.data?.error || error.message
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-white">
      <h4 className="text-sm font-medium text-gray-900 mb-4">Intercom Configuration</h4>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            App ID (from your Intercom workspace settings)
          </label>
          <input
            type="text"
            value={formData.app_id}
            onChange={(e) => setFormData(prev => ({ ...prev, app_id: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="swlbxc2u"
            required
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Access Token (from your Intercom Developer Hub)
          </label>
          <div className="flex space-x-2">
            <input
              type="password"
              value={formData.access_token}
              onChange={(e) => setFormData(prev => ({ ...prev, access_token: e.target.value }))}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Your Intercom access token"
              required
            />
            <button
              type="button"
              onClick={handleTest}
              disabled={testing || !formData.access_token}
              className="px-3 py-2 text-xs bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {testing ? 'Testing...' : 'Test'}
            </button>
          </div>
        </div>

        {testResult && (
          <div className={`p-3 rounded-md text-sm ${
            testResult.success 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {testResult.success ? (
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Connection successful! Connected as: {testResult.data?.name || 'Unknown'}
              </div>
            ) : (
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                Connection failed: {testResult.error}
              </div>
            )}
          </div>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <div className="text-xs text-blue-800">
            <p className="font-medium mb-1">Setup Instructions:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Go to your Intercom workspace settings → Installation</li>
              <li>Copy your App ID (e.g., "swlbxc2u")</li>
              <li>Visit Intercom Developer Hub → Your Apps → Create App</li>
              <li>Generate an access token with "Read conversations" permission</li>
              <li>Configure webhooks in Intercom:</li>
              <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                <li>URL: Use the webhook URL shown above</li>
                <li>Topics: conversation.user.created, conversation.user.replied, conversation.admin.replied</li>
              </ul>
            </ol>
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
          <p className="text-xs font-medium text-gray-700 mb-1">Webhook URL for Intercom:</p>
          {settings.webhook_url ? (
            <div className="flex items-center space-x-2">
              <code className="flex-1 px-2 py-1 text-xs bg-white border border-gray-300 rounded font-mono">
                {settings.webhook_url}
              </code>
              <button
                type="button"
                onClick={() => navigator.clipboard.writeText(settings.webhook_url)}
                className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                title="Copy to clipboard"
              >
                Copy
              </button>
            </div>
          ) : (
            <div className="text-xs text-gray-500 italic">
              Webhook URL will appear after saving configuration
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !formData.app_id || !formData.access_token}
            className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </form>
    </div>
  );
};

// Mixpanel Configuration Form Component
const MixpanelConfigForm = ({ settings, onSave, onCancel, onTest }) => {
  const [formData, setFormData] = useState({
    project_token: settings.project_token || '',
    api_secret: settings.api_secret || '',
    sync_enabled: settings.sync_enabled !== false,
    auto_create_leads: settings.auto_create_leads || false,
    sync_frequency: settings.sync_frequency || 'daily',
    lookback_days: settings.lookback_days || 1
  });
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [manualSyncing, setManualSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  const handleManualSync = async () => {
    setManualSyncing(true);
    setSyncResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/mixpanel-client/sync/manual', {}, {
        headers: { 'x-auth-token': token }
      });
      
      setSyncResult({
        success: true,
        message: response.data.message,
        leadsCreated: response.data.leadsCreated || 0,
        emailsProcessed: response.data.emailsProcessed || 0
      });
    } catch (error) {
      setSyncResult({
        success: false,
        message: error.response?.data?.error || error.message
      });
    } finally {
      setManualSyncing(false);
    }
  };

  const handleTest = async () => {
    if (!formData.project_token || !formData.api_secret) {
      alert('Please enter both project token and API secret first');
      return;
    }

    setTesting(true);
    setTestResult(null);
    
    try {
      const result = await onTest(formData.project_token, formData.api_secret);
      setTestResult(result);
    } catch (error) {
      setTestResult({
        success: false,
        error: error.response?.data?.error || error.message
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-white">
      <h4 className="text-sm font-medium text-gray-900 mb-4">Mixpanel Configuration</h4>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Project Token (from your Mixpanel project settings)
          </label>
          <input
            type="text"
            value={formData.project_token}
            onChange={(e) => setFormData(prev => ({ ...prev, project_token: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
            placeholder="e.g., a1b2c3d4e5f6g7h8i9j0"
            required
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            API Secret (from your Mixpanel project settings)
          </label>
          <div className="flex space-x-2">
            <input
              type="password"
              value={formData.api_secret}
              onChange={(e) => setFormData(prev => ({ ...prev, api_secret: e.target.value }))}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
              placeholder="Your Mixpanel API secret"
              required
            />
            <button
              type="button"
              onClick={handleTest}
              disabled={testing || !formData.project_token || !formData.api_secret}
              className="px-3 py-2 text-xs bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {testing ? 'Testing...' : 'Test'}
            </button>
          </div>
        </div>

        <div className="space-y-4 border-t border-gray-200 pt-4">
          <h5 className="text-sm font-medium text-gray-900">Sync Settings</h5>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="sync_enabled"
              checked={formData.sync_enabled}
              onChange={(e) => setFormData(prev => ({ ...prev, sync_enabled: e.target.checked }))}
              className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
            />
            <label htmlFor="sync_enabled" className="ml-2 block text-xs text-gray-700">
              Enable event syncing to view Mixpanel activities in lead timelines
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="auto_create_leads"
              checked={formData.auto_create_leads}
              onChange={(e) => setFormData(prev => ({ ...prev, auto_create_leads: e.target.checked }))}
              className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
            />
            <label htmlFor="auto_create_leads" className="ml-2 block text-xs text-gray-700">
              Automatically create new leads from Mixpanel events
            </label>
          </div>

          {formData.auto_create_leads && (
            <div className="ml-6 space-y-3 p-3 bg-green-50 rounded-md">
              <div className="text-xs text-green-700">
                <p className="font-medium mb-2">✅ Automated lead creation is enabled</p>
                <p>• Checks for new Mixpanel visitors every hour</p>
                <p>• Creates CRM leads for new email addresses found in events</p>
                <p>• Leads are assigned using round-robin distribution</p>
              </div>

              {formData.project_token && formData.api_secret && (
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={handleManualSync}
                    disabled={manualSyncing}
                    className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-md hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {manualSyncing ? 'Testing...' : 'Test Sync Now'}
                  </button>
                </div>
              )}

              {syncResult && (
                <div className={`p-2 rounded-md text-xs ${
                  syncResult.success 
                    ? 'bg-green-50 text-green-800 border border-green-200' 
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {syncResult.success ? (
                    <div>
                      <p className="font-medium">✅ {syncResult.message}</p>
                      <p>Leads created: {syncResult.leadsCreated}, Emails processed: {syncResult.emailsProcessed}</p>
                    </div>
                  ) : (
                    <p>❌ {syncResult.message}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {testResult && (
          <div className={`p-3 rounded-md text-sm ${
            testResult.success 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {testResult.success ? (
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Connection successful! Can access Mixpanel data.
              </div>
            ) : (
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                Connection failed: {testResult.error}
              </div>
            )}
          </div>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <div className="text-xs text-blue-800">
            <p className="font-medium mb-1">Setup Instructions:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Go to your Mixpanel project settings</li>
              <li>Navigate to "Project Settings" → "API Keys"</li>
              <li>Copy your Project Token</li>
              <li>Copy or create a new API Secret</li>
              <li>Make sure your API secret has "Export" permissions</li>
            </ol>
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
          <p className="text-xs font-medium text-gray-700 mb-1">How it works:</p>
          <p className="text-xs text-gray-600">
            When viewing leads, the system will query your Mixpanel data to show recent website/app activity 
            for that user in the Recent Activity feed. Events are matched by email address and cached for 5 minutes 
            to improve performance.
          </p>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !formData.project_token || !formData.api_secret}
            className="px-4 py-2 text-sm bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ModulesSettings;